// Extra CMS slots for the 'terms' page — added to make previously-hardcoded
// public marketing copy editable. Concatenated into CMS_SLOTS via ./index.
// Each default MUST equal the exact copy the component rendered before wiring,
// so the public page is byte-identical until an admin edits it.
//
// Note: the Exchange Covenant bullet list (terms.covenant_items) and the hero
// background photo (terms.hero_image) already live in the base registry
// (slots.ts). Everything else on the page is declared here.
import type { SlotDef } from '../slots';

export const termsExtra: SlotDef[] = [
  // ─── Hero ─────────────────────────────────────────────────────────────
  {
    key: 'terms.last_updated',
    page: 'terms',
    kind: 'markdown',
    label: 'Hero: last-updated date',
    helpText: 'Shown in the hero eyebrow after the fixed "Last updated · " prefix.',
    default: '9 May 2026',
  },
  {
    key: 'terms.hero_title',
    page: 'terms',
    kind: 'markdown',
    label: 'Hero: title',
    default: 'Terms of Service.',
  },
  {
    key: 'terms.hero_lede',
    page: 'terms',
    kind: 'markdown',
    label: 'Hero: lede',
    default: 'The rules you agree to when you use WWAAVE, written as plainly as the law allows.',
  },

  // ─── Draft banner ─────────────────────────────────────────────────────
  {
    key: 'terms.review_banner_title',
    page: 'terms',
    kind: 'markdown',
    label: 'Draft banner: title',
    default: 'Draft, under legal review',
  },
  {
    key: 'terms.review_banner_body',
    page: 'terms',
    kind: 'markdown',
    label: 'Draft banner: body',
    helpText: 'The launch-blocking disclaimer shown in the yellow alert at the top of the page.',
    default:
      'This is a placeholder Terms of Service pending review by counsel. The final version will replace this page before WWAAVE launches publicly.',
  },

  // ─── Section 1 · About WWAAVE ─────────────────────────────────────────
  {
    key: 'terms.about_eyebrow',
    page: 'terms',
    kind: 'markdown',
    label: 'About: eyebrow',
    default: 'Section 1',
  },
  {
    key: 'terms.about_title',
    page: 'terms',
    kind: 'markdown',
    label: 'About: title',
    default: 'About WWAAVE.',
  },
  {
    key: 'terms.about_body',
    page: 'terms',
    kind: 'markdown',
    label: 'About: body',
    helpText:
      'The legal entity name "WWAAVE PTY LTD (ACN 689 639 423)." is appended after this text and stays fixed.',
    default:
      'WWAAVE is a marketplace where touring artists and host venues connect for non-cash, mutual exchanges. WWAAVE is operated by',
  },

  // ─── Section 2 · The Exchange Promise (bullet list already a slot) ────
  {
    key: 'terms.exchange_promise_eyebrow',
    page: 'terms',
    kind: 'markdown',
    label: 'Exchange Promise: eyebrow',
    default: 'Section 2',
  },
  {
    key: 'terms.exchange_promise_title',
    page: 'terms',
    kind: 'markdown',
    label: 'Exchange Promise: title',
    default: 'The Exchange Promise.',
  },
  {
    key: 'terms.exchange_promise_intro',
    page: 'terms',
    kind: 'markdown',
    label: 'Exchange Promise: intro line (above the list)',
    default: 'By using wwaave, you agree that:',
  },
  {
    key: 'terms.exchange_promise_outro',
    page: 'terms',
    kind: 'markdown',
    label: 'Exchange Promise: closing line (before link)',
    helpText:
      'The link to the Our Ethos page sits between this and the "after link" text. Leave all three blank to hide the closing line.',
    default: 'The full promise is on the',
  },
  {
    // Renamed 2026-08-18 — the destination page is "Our Ethos" now (the URL is
    // unchanged). ⚠️ The surrounding sentence still reads "The full promise is
    // on the Our Ethos page", and the Promise section was removed from that
    // page the same day, so this sentence now over-promises. Reword the three
    // outro slots (or restore the promise copy somewhere) before launch.
    key: 'terms.exchange_promise_link_label',
    page: 'terms',
    kind: 'markdown',
    label: 'Exchange Promise: link label',
    helpText: 'The clickable words. Always points at the Our Ethos page.',
    default: 'Our Ethos',
  },
  {
    key: 'terms.exchange_promise_outro_after',
    page: 'terms',
    kind: 'markdown',
    label: 'Exchange Promise: closing line (after link)',
    helpText: 'Continues immediately after the link — start it with a space if you need one.',
    default: ' page.',
  },

  // ─── Section 3 · Eligibility ──────────────────────────────────────────
  {
    key: 'terms.eligibility_eyebrow',
    page: 'terms',
    kind: 'markdown',
    label: 'Eligibility: eyebrow',
    default: 'Section 3',
  },
  {
    key: 'terms.eligibility_title',
    page: 'terms',
    kind: 'markdown',
    label: 'Eligibility: title',
    default: 'Eligibility.',
  },
  {
    key: 'terms.eligibility_body',
    page: 'terms',
    kind: 'markdown',
    label: 'Eligibility: body',
    default:
      'You must be at least 18 years old and able to form a binding contract under the laws of your jurisdiction. WWAAVE is currently open to people based in Australia.',
  },

  // ─── Section 4 · Subscriptions ────────────────────────────────────────
  {
    key: 'terms.subscriptions_eyebrow',
    page: 'terms',
    kind: 'markdown',
    label: 'Subscriptions: eyebrow',
    default: 'Section 4',
  },
  {
    key: 'terms.subscriptions_title',
    page: 'terms',
    kind: 'markdown',
    label: 'Subscriptions: title',
    default: 'Subscriptions.',
  },
  {
    key: 'terms.subscriptions_body',
    page: 'terms',
    kind: 'markdown',
    label: 'Subscriptions: body',
    helpText: 'Includes the $19/mo founding-cohort price and the 50-venue cap.',
    default:
      "Hosting on WWAAVE requires a subscription. The founding-cohort rate is $19 AUD/month after a free first year, capped at the first 50 venues, and locked at that rate for the duration of the membership. The standard rate is $19 AUD/month from day one, billed monthly or annually. We'll always tell you when a payment is about to be charged.",
  },

  // ─── Section 5 · Content and conduct ──────────────────────────────────
  {
    key: 'terms.conduct_eyebrow',
    page: 'terms',
    kind: 'markdown',
    label: 'Content and conduct: eyebrow',
    default: 'Section 5',
  },
  {
    key: 'terms.conduct_title',
    page: 'terms',
    kind: 'markdown',
    label: 'Content and conduct: title',
    default: 'Content and conduct.',
  },
  {
    key: 'terms.conduct_body',
    page: 'terms',
    kind: 'markdown',
    label: 'Content and conduct: body',
    default:
      'You own the content you post: photos, descriptions, letters, reviews. By posting, you grant WWAAVE a non-exclusive licence to display that content within the platform. We may remove content that breaches these terms or the promise.',
  },

  // ─── Section 6 · Disputes between people ──────────────────────────────
  {
    key: 'terms.disputes_eyebrow',
    page: 'terms',
    kind: 'markdown',
    label: 'Disputes: eyebrow',
    default: 'Section 6',
  },
  {
    key: 'terms.disputes_title',
    page: 'terms',
    kind: 'markdown',
    label: 'Disputes: title',
    default: 'Disputes between people.',
  },
  {
    key: 'terms.disputes_body',
    page: 'terms',
    kind: 'markdown',
    label: 'Disputes: body (before link)',
    helpText:
      'The inline "dispute resolution policy" link (to /dispute-resolution) sits between this and the "after link" text.',
    default:
      'When something goes wrong with an exchange, our trust & safety team will mediate per the',
  },
  {
    key: 'terms.disputes_link_label',
    page: 'terms',
    kind: 'markdown',
    label: 'Disputes: link label',
    helpText: 'The clickable words. Always points at the dispute resolution page.',
    default: 'dispute resolution policy',
  },
  {
    key: 'terms.disputes_body_after',
    page: 'terms',
    kind: 'markdown',
    label: 'Disputes: body (after link)',
    helpText: 'Continues immediately after the "dispute resolution policy" link.',
    default:
      '. WWAAVE makes the final decision on platform-side outcomes (warnings, suspensions, review releases). Civil and legal remedies between parties are between the parties.',
  },

  // ─── Section 7 · Termination ──────────────────────────────────────────
  {
    key: 'terms.termination_eyebrow',
    page: 'terms',
    kind: 'markdown',
    label: 'Termination: eyebrow',
    default: 'Section 7',
  },
  {
    key: 'terms.termination_title',
    page: 'terms',
    kind: 'markdown',
    label: 'Termination: title',
    default: 'Termination.',
  },
  {
    key: 'terms.termination_body',
    page: 'terms',
    kind: 'markdown',
    label: 'Termination: body',
    default:
      "You can close your account at any time. We can suspend or close an account that breaches these terms or the promise. We'll tell you the reason where we can.",
  },

  // ─── Section 8 · Liability ────────────────────────────────────────────
  {
    key: 'terms.liability_eyebrow',
    page: 'terms',
    kind: 'markdown',
    label: 'Liability: eyebrow',
    default: 'Section 8',
  },
  {
    key: 'terms.liability_title',
    page: 'terms',
    kind: 'markdown',
    label: 'Liability: title',
    default: 'Liability.',
  },
  {
    key: 'terms.liability_body',
    page: 'terms',
    kind: 'markdown',
    label: 'Liability: body',
    default:
      "WWAAVE is the introduction layer. We don't verify the safety of every venue or the conduct of every artist beyond our verification process. Use your judgment, read reviews, and tell us if something is off.",
  },

  // ─── Section 9 · Governing law ────────────────────────────────────────
  {
    key: 'terms.governing_law_eyebrow',
    page: 'terms',
    kind: 'markdown',
    label: 'Governing law: eyebrow',
    default: 'Section 9',
  },
  {
    key: 'terms.governing_law_title',
    page: 'terms',
    kind: 'markdown',
    label: 'Governing law: title',
    default: 'Governing law.',
  },
  {
    key: 'terms.governing_law_body',
    page: 'terms',
    kind: 'markdown',
    label: 'Governing law: body',
    default:
      "These terms are governed by the laws of Queensland, Australia. Any disputes that can't be resolved through our internal process will be heard in the courts of Queensland.",
  },
  {
    key: 'terms.legal_entity_line',
    page: 'terms',
    kind: 'markdown',
    label: 'Governing law: legal entity / contact line',
    helpText: 'Keep in sync with the site-footer legal line.',
    default: 'WWAAVE PTY LTD · ACN 689 639 423 · hello@wwaave.com',
  },

  // ─── "Read alongside" closing tiles ───────────────────────────────────
  {
    key: 'terms.next_title',
    page: 'terms',
    kind: 'markdown',
    label: 'Read alongside: title',
    default: 'Read alongside.',
  },
  {
    key: 'terms.next_lede',
    page: 'terms',
    kind: 'markdown',
    label: 'Read alongside: lede',
    default: 'The privacy policy and the dispute resolution process round out the picture.',
    // Closing-block subheading removed site-wide at Richie's request — no longer
    // rendered. Retired (not deleted) so any override doesn't orphan; drops out
    // of the admin CMS editor.
    deprecated: true,
  },
  {
    key: 'terms.next_tile1_title',
    page: 'terms',
    kind: 'markdown',
    label: 'Read alongside: tile 1 title (Privacy)',
    default: 'Privacy Policy',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'terms.next_tile1_desc',
    page: 'terms',
    kind: 'markdown',
    label: 'Read alongside: tile 1 description (Privacy)',
    default: 'What we collect, what we never do with it, your rights.',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'terms.next_tile2_title',
    page: 'terms',
    kind: 'markdown',
    label: 'Read alongside: tile 2 title (Dispute resolution)',
    default: 'Dispute resolution',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'terms.next_tile2_desc',
    page: 'terms',
    kind: 'markdown',
    label: 'Read alongside: tile 2 description (Dispute resolution)',
    default: 'How we handle exchanges that go wrong.',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'terms.next_tile3_title',
    page: 'terms',
    kind: 'markdown',
    label: 'Read alongside: tile 3 title (The WWAAVE Promise)',
    default: 'The WWAAVE Promise',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'terms.next_tile3_desc',
    page: 'terms',
    kind: 'markdown',
    label: 'Read alongside: tile 3 description (The WWAAVE Promise)',
    default: 'The principles the terms make formal.',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
];
